/**
 * The ingredient table — same column layout as the original Shared HTML.
 *
 * Each row's name cell has an autocomplete dropdown backed by the library.
 * Selecting from autocomplete also fills INCI, supplier, and phase
 * (when those columns are empty). Amount cells turn amber if outside the
 * ingredient's typical pct range.
 */

import type { BatchData, IngredientRow } from '../types';
import { PHASES } from '../lib/defaultBatch';
import IngredientAutocomplete from './IngredientAutocomplete';

interface Props {
  rows: IngredientRow[];
  percents: number[];
  total: number;
  onRowsChange: (rows: IngredientRow[]) => void;
  onFocusField: (fieldId: string | null) => void;
}

export default function IngredientTable({
  rows,
  percents,
  total,
  onRowsChange,
  onFocusField,
}: Props) {
  function setCell(idx: number, patch: Partial<IngredientRow>) {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onRowsChange(next);
  }

  function addRow() {
    onRowsChange([
      ...rows,
      { ingredient: '', inci: '', supplier: '', phase: 'Water', amount: '', notes: '' },
    ]);
  }

  function deleteRow(idx: number) {
    onRowsChange(rows.filter((_, i) => i !== idx));
  }

  return (
    <div className="table-card">
      <div className="table-head-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="card-title">Ingredient Formula</span>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
          Amount in grams · % auto-calculates
        </span>
      </div>
      <table className="ing-table">
        <colgroup>
          <col className="t-num" />
          <col className="t-ing" />
          <col className="t-inci" />
          <col className="t-sup" />
          <col className="t-phase" />
          <col className="t-amt" />
          <col className="t-pct" />
          <col className="t-notes" />
          <col className="t-del" />
        </colgroup>
        <thead>
          <tr>
            <th className="t-num">#</th>
            <th className="t-ing">Ingredient</th>
            <th className="t-inci">INCI Name</th>
            <th className="t-sup">Supplier</th>
            <th className="t-phase">Phase</th>
            <th className="t-amt">Amount (g)</th>
            <th className="t-pct">%</th>
            <th className="t-notes">Notes / function</th>
            <th className="t-del" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx}>
              <td className="t-num">{idx + 1}</td>
              <td>
                <IngredientAutocomplete
                  value={r.ingredient}
                  onChange={(v) => setCell(idx, { ingredient: v })}
                  onSelect={(it) => {
                    setCell(idx, {
                      ingredient: it.name,
                      inci: r.inci || it.inci || '',
                      supplier: r.supplier || it.default_supplier || '',
                      phase: r.phase || it.default_phase || 'Water',
                    });
                  }}
                  onFocus={() => onFocusField(`row.${idx}.ingredient`)}
                  onBlur={() => onFocusField(null)}
                />
              </td>
              <td>
                <input
                  className="cell-in"
                  placeholder="INCI"
                  value={r.inci}
                  onChange={(e) => setCell(idx, { inci: e.target.value })}
                  onFocus={() => onFocusField(`row.${idx}.inci`)}
                  onBlur={() => onFocusField(null)}
                />
              </td>
              <td>
                <input
                  className="cell-in"
                  placeholder="Supplier"
                  value={r.supplier}
                  onChange={(e) => setCell(idx, { supplier: e.target.value })}
                  onFocus={() => onFocusField(`row.${idx}.supplier`)}
                  onBlur={() => onFocusField(null)}
                />
              </td>
              <td>
                <select
                  className="cell-sel"
                  value={r.phase}
                  onChange={(e) => setCell(idx, { phase: e.target.value })}
                  onFocus={() => onFocusField(`row.${idx}.phase`)}
                  onBlur={() => onFocusField(null)}
                >
                  {PHASES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="cell-in cell-amt"
                  placeholder="0.00"
                  value={r.amount}
                  onChange={(e) => setCell(idx, { amount: e.target.value })}
                  onFocus={() => onFocusField(`row.${idx}.amount`)}
                  onBlur={() => onFocusField(null)}
                />
              </td>
              <td className="t-pct">
                {total > 0 && r.amount
                  ? (percents[idx] ?? 0).toFixed(1) + '%'
                  : '—'}
              </td>
              <td>
                <input
                  className="cell-in"
                  placeholder="Notes…"
                  value={r.notes}
                  onChange={(e) => setCell(idx, { notes: e.target.value })}
                  onFocus={() => onFocusField(`row.${idx}.notes`)}
                  onBlur={() => onFocusField(null)}
                />
              </td>
              <td>
                <button
                  className="del-btn"
                  onClick={() => deleteRow(idx)}
                  aria-label="Delete row"
                  type="button"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="add-row-btn" onClick={addRow} type="button">
        + Add ingredient
      </button>
    </div>
  );
}

/** Helper: given a batch's row, infer a percent. */
export function rowPercent(row: IngredientRow, total: number): number {
  if (total <= 0) return 0;
  return ((parseFloat(row.amount) || 0) / total) * 100;
}
